import {
  PerformanceMeasure,
  ScenarioPerformanceData,
  endAndReportPerformanceMeasure,
  requestAnimationFrameAfterStyleAndLayout,
  startPerformanceMeasure,
} from '@ms/yammer-telemetry';
import {
  PageLoadPerformanceEventProperties,
  PerformanceEventName,
  PerformanceEventProperties,
} from '@ms/yammer-telemetry-support';

import { emitPageLoadEvent } from './loadEvents';

type PageLoadPerformanceEventName = Extract<PerformanceEventName, 'page_load' | 'cached_first_page_load'>;
interface PageLoadPerformanceMeasureStore {
  readonly measures: Record<string, ScenarioLoadPerformanceData>;
  readonly completed: Record<string, true>;
  // eslint-disable-next-line @ms/prefer-readonly-properties
  mostRecentPageLoadMeasure?: PerformanceMeasure<Partial<PageLoadPerformanceEventProperties>>;
}

/**
 * TODO: Periodically clear out old measures to keep memory usage low
 * https://dev.azure.com/yammer/engineering/_sprints/taskboard/WebXP/engineering/2024/Q1/Q1S2?workitem=294525
 */
const store: PageLoadPerformanceMeasureStore = { measures: {}, completed: {} };

// Scenario timeout (30 seconds)
const pageLoadMeasureTimeout = 30_000;

interface PartialPageLoadPerformanceMeasureOptions {
  readonly eventName: PageLoadPerformanceEventName;
  readonly pageLoadId: number;
  readonly eventProperties?: PerformanceEventProperties<Partial<PageLoadPerformanceEventProperties>>;
}
type UpdatePageLoadPerformanceMeasure = (options: PartialPageLoadPerformanceMeasureOptions) => void;

type StartPageLoadPerformanceMeasure = (options: PartialPageLoadPerformanceMeasureOptions) => void;

/**
 * Starts a page performance measure event, which can be ended asynchronously by its pageLoadId. Events will cancel
 * and report as "timedOut" after 30 seconds. Uses startPerformanceMeasure and endScenarioPerformanceMeasure
 * internally to execute the performance measurements.
 * Returns true if successfully started, false if the event was already started.
 */
export const startPageLoadPerformanceMeasure: StartPageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties = {},
}) => {
  const isMeasureActiveForPageLoadId = getPerformanceEntry(eventName, pageLoadId);
  if (isMeasureActiveForPageLoadId) {
    return;
  }

  const isMeasureCompletedForPageLoadId = isPerformanceEventMeasureMarkedAsCompleted(eventName, pageLoadId);
  if (isMeasureCompletedForPageLoadId) {
    return;
  }

  // Start and store measure in scenario collection
  const measure: PerformanceMeasure<Partial<PageLoadPerformanceEventProperties>> = {
    ...startPerformanceMeasure(eventName),
    eventProperties,
  };
  const entry: ScenarioLoadPerformanceData = {
    measure,
    timer: window.setTimeout(() => {
      const eventProperties: PerformanceEventProperties<
        Pick<PageLoadPerformanceEventProperties, 'timedOut' | 'endedWithError' | 'interrupted'>
      > = {
        timedOut: true,
        endedWithError: false,
        interrupted: false,
      };

      endAndReportPageLoadPerformanceMeasureNowIfNotCompleted({
        eventName,
        pageLoadId,
        eventProperties,
      });
    }, pageLoadMeasureTimeout),
  };

  setPerformanceEventEntry(eventName, pageLoadId, entry);

  if (eventName === 'page_load') {
    store.mostRecentPageLoadMeasure = measure;
  }
};

/**
 * Updates event properties of an in-progress scenario performance measure. This allows for correct reporting of
 * dynamic properties (like didVisibilityStateChange) in case the event times out.
 */
export const updatePageLoadPerformanceMeasureEventProperties: UpdatePageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties = {},
}) => {
  const entry = getPerformanceEntry(eventName, pageLoadId);

  if (entry) {
    const measure = {
      ...entry.measure,
      eventProperties: {
        ...(entry.measure.eventProperties as PageLoadPerformanceEventProperties),
        ...eventProperties,
      },
    };

    const updatedData: ScenarioLoadPerformanceData = {
      ...entry,
      measure,
    };

    setPerformanceEventEntry(eventName, pageLoadId, updatedData);

    // istanbul ignore else
    if (eventName === 'page_load') {
      store.mostRecentPageLoadMeasure = measure;
    }
  }
};

export const startOrUpdatePageLoadPerformanceMeasureEventProperties: StartPageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties,
}) => {
  const entry = getPerformanceEntry(eventName, pageLoadId);

  if (entry) {
    updatePageLoadPerformanceMeasureEventProperties({ eventName, pageLoadId, eventProperties });
  } else {
    startPageLoadPerformanceMeasure({ eventName, pageLoadId, eventProperties });
  }
};

type EndAndReportPageLoadPerformanceMeasure = (options: PartialPageLoadPerformanceMeasureOptions) => void;

/**
 * Schedules a scenario performance event to be ended and reported after the full render + paint. Although the event
 * will be reported asynchronously to account for real user experience, it will be immediately tracked as "reported"
 * to prevent other async code from canceling or reporting this event.
 */
export const scheduleEndAndReportPageLoadPerformanceMeasureAfterStyleAndLayout: EndAndReportPageLoadPerformanceMeasure =
  (options) => {
    markPerformanceEventEntryAsCompleted(options.eventName, options.pageLoadId);

    requestAnimationFrameAfterStyleAndLayout(() => {
      endAndReportPageLoadPerformanceMeasureNow(options);
    });
  };

/**
 * Ends a scenario performance event and reports it to telemetry. It may be called automatically if a timeout is
 * reached (timedOut) or if a new scenario with the same event name is started (interrupted).
 */
export const endAndReportPageLoadPerformanceMeasureNowIfNotCompleted: EndAndReportPageLoadPerformanceMeasure = (
  options
) => {
  if (isPerformanceEventMeasureMarkedAsCompleted(options.eventName, options.pageLoadId)) {
    return;
  }

  endAndReportPageLoadPerformanceMeasureNow(options);
};

/**
 * Ends a scenario performance event and reports it to telemetry immediately, even if already marked as completed.
 */
const endAndReportPageLoadPerformanceMeasureNow: EndAndReportPageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties = {},
}) => {
  const scenarioData = getPerformanceEntry(eventName, pageLoadId);

  if (scenarioData) {
    const measure: PerformanceMeasure<Partial<PageLoadPerformanceEventProperties>> = {
      ...scenarioData.measure,
      eventProperties: {
        ...scenarioData.measure.eventProperties,
        ...eventProperties,
      },
    };

    endAndReportPerformanceMeasure(measure);
    clearPerformanceEventEntry(eventName, pageLoadId, scenarioData);
    markPerformanceEventEntryAsCompleted(eventName, pageLoadId);

    // istanbul ignore else
    if (eventName === 'page_load') {
      store.mostRecentPageLoadMeasure = measure;
    }

    triggerPageLoadEvent(
      measure.eventProperties as PerformanceEventProperties<
        Pick<PageLoadPerformanceEventProperties, 'endedWithError' | 'timedOut' | 'interrupted' | 'isExpectedError'>
      >
    );
  }
};

const getKey = (eventName: PageLoadPerformanceEventName, pageLoadId: number) => `${eventName}-${pageLoadId}`;

type ScenarioLoadPerformanceData = ScenarioPerformanceData<Partial<PageLoadPerformanceEventProperties>>;

const clearPerformanceEventEntry = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number,
  scenarioData: ScenarioLoadPerformanceData
) => {
  window.clearTimeout(scenarioData.timer);

  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete store.measures[getKey(eventName, pageLoadId)];
};

type GetPerformanceEventEntry = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number
) => ScenarioLoadPerformanceData | undefined;
const getPerformanceEntry: GetPerformanceEventEntry = (eventName, pageLoadId) =>
  store.measures[getKey(eventName, pageLoadId)];

const setPerformanceEventEntry = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number,
  entry: ScenarioLoadPerformanceData
) => {
  store.measures[getKey(eventName, pageLoadId)] = entry;
};

const markPerformanceEventEntryAsCompleted = (eventName: PageLoadPerformanceEventName, pageLoadId: number) => {
  store.completed[getKey(eventName, pageLoadId)] = true;
};

interface ClearCompletedPerformanceEventMeasureOptions {
  readonly eventName: PageLoadPerformanceEventName;
  readonly pageLoadId: number;
}
type ClearCompletedPerformanceEventMeasure = (props: ClearCompletedPerformanceEventMeasureOptions) => void;
export const clearCompletedPerformanceEventMeasure: ClearCompletedPerformanceEventMeasure = ({
  eventName,
  pageLoadId,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete store.completed[getKey(eventName, pageLoadId)];
};

type IsPerformanceEventMeasureMarkedAsCompleted = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number
) => boolean;
const isPerformanceEventMeasureMarkedAsCompleted: IsPerformanceEventMeasureMarkedAsCompleted = (
  eventName,
  pageLoadId
) => store.completed[getKey(eventName, pageLoadId)] || false;

export const getMostRecentPageLoadMeasure = () => store.mostRecentPageLoadMeasure;

type TriggerPageLoadEvent = (
  options: PerformanceEventProperties<
    Pick<PageLoadPerformanceEventProperties, 'endedWithError' | 'timedOut' | 'interrupted' | 'isExpectedError'>
  >
) => void;
const triggerPageLoadEvent: TriggerPageLoadEvent = ({ endedWithError, timedOut, interrupted, isExpectedError }) => {
  if (timedOut) {
    emitPageLoadEvent('TimedOut');
  } else if (interrupted) {
    emitPageLoadEvent('Interrupted');
  } else if (endedWithError) {
    if (isExpectedError) {
      emitPageLoadEvent('FailedRecoverable');
    } else {
      emitPageLoadEvent('Failed');
    }
  } else {
    emitPageLoadEvent('Loaded');
  }
};
